import { useState, useRef, useEffect } from "react";
import FullscreenIcon from "../../assets/svg/FullscreenIcon";
import CustomPopUp from "./CustomPopup";
import CustomImage from "./CustomImage";
import useMobile from "../../utils/helpers/useMobile";

interface SignatureViewerProps {
  imageURL: string;
  alt?: string;
  className?: string;
  containerClassName?: string;
  maxHeight?: string;
  maxWidth?: string;
}

function SignatureViewer({
  imageURL,
  alt = "Signature",
  className = "",
  containerClassName = "",
  maxHeight = "300px",
  maxWidth = "100%",
}: Readonly<SignatureViewerProps>): JSX.Element {
  const isMobile = useMobile();
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpenFullscreen = () => {
    setShowFullscreen(true);
  };

  const handleCloseFullscreen = () => {
    setShowFullscreen(false);
  };

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    // Check if image loads successfully
    if (imageURL) {
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
        setImageError(false);
      };
      img.onerror = () => {
        setImageLoaded(false);
        setImageError(true);
      };
      img.src = imageURL;
    } else {
      setImageError(true);
    }
  }, [imageURL]);

  return (
    <>
      <div className={`flex flex-col relative ${containerClassName}`}>
        {imageLoaded && (
          <button
            type="button"
            onClick={handleOpenFullscreen}
            className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white border border-gray-300 rounded-md p-2 shadow-sm transition-all hover:shadow-md"
            title="View Fullscreen"
          >
            <FullscreenIcon className="w-4 h-4" stroke="#667085" />
          </button>
        )}
        <div
          ref={containerRef}
          className="relative overflow-auto border border-roundedInputBorder rounded-lg bg-sideNavGray flex justify-center items-center"
          style={{
            maxWidth,
            maxHeight,
            minHeight: "200px",
          }}
        >
          {!imageLoaded && imageURL && (
            <div className="flex items-center justify-center w-full h-full min-h-[200px]">
              <div className="text-gray-400 text-sm">Loading signature...</div>
            </div>
          )}
          <CustomImage
            src={imageURL}
            alt={alt}
            className={`${className} object-contain max-w-full max-h-full w-auto h-auto ${imageError || !imageURL ? 'opacity-40' : ''}`}
          />
        </div>
      </div>

      {showFullscreen && (
        <CustomPopUp
          popUpTitle="Signature"
          hidePopUp={handleCloseFullscreen}
          hideClose={false}
        >
          <div className="flex flex-col w-full">
            {/* Fullscreen Image Container */}
            <div
              ref={containerRef}
              className="relative overflow-auto border border-roundedInputBorder rounded-lg bg-sideNavGray flex justify-center items-center w-full"
              style={{
                maxHeight: isMobile ? "60vh" : "80vh",
                minHeight: isMobile ? "300px" : "500px",
                width: "100%",
              }}
            >
              {!imageLoaded && imageURL && (
                <div
                  className="flex items-center justify-center w-full h-full"
                  style={{
                    minHeight: isMobile ? "300px" : "500px",
                  }}
                >
                  <div className="text-gray-400 text-sm">Loading signature...</div>
                </div>
              )}
              <CustomImage
                src={imageURL}
                alt={alt}
                className={`object-contain max-w-full max-h-full w-auto h-auto ${imageError || !imageURL ? 'opacity-40' : ''}`}
              />
            </div>
          </div>
        </CustomPopUp>
      )}
    </>
  );
}

export default SignatureViewer;

